import { graphql, useStaticQuery } from 'gatsby';
import { useMemo } from 'react';
import type { CourseData, LectureData } from '@/domain/precon-supporter/types';

/**
 * MDXデータからコース情報を構築
 */
function buildCoursesFromMdx(data: Queries.AllLecturesQuery): CourseData[] {
  const basicLectures: LectureData[] = [];
  const advancedLectures: LectureData[] = [];

  data.allMdx.nodes.forEach(node => {
    const frontmatter = node.frontmatter;
    if (!frontmatter) return;

    const lecture: LectureData = {
      id: frontmatter.lectureId || '',
      courseId: (frontmatter.courseId as 'basic' | 'advanced') || 'basic',
      title: frontmatter.title || '',
      description: frontmatter.overview?.join('\n') || '',
      videoUrl: frontmatter.videoUrl || '',
      thumbnail: frontmatter.thumbnail || undefined,
      duration: 30, // デフォルト30分
      isRequired: frontmatter.isRequired ?? true,
      order: frontmatter.order || 0,
      instructors: frontmatter.instructors
        ? frontmatter.instructors.map(instructor => ({
            name: instructor?.name || '',
            affiliation: instructor?.affiliation || '',
            type: instructor?.type as 'in-person' | 'online' | undefined,
          }))
        : undefined,
      targetAudience:
        frontmatter.targetAudience as LectureData['targetAudience'],
    };

    if (lecture.courseId === 'basic') {
      basicLectures.push(lecture);
    } else if (lecture.courseId === 'advanced') {
      advancedLectures.push(lecture);
    }
  });

  // isRequiredでグループ分けしてからorderでソート
  // Required講座が先、その後Optional講座という順序を保証
  basicLectures.sort((a, b) => {
    // isRequiredでまず比較（trueが先）
    if (a.isRequired !== b.isRequired) {
      return a.isRequired ? -1 : 1;
    }
    // 同じグループ内ではorderで比較
    return a.order - b.order;
  });

  advancedLectures.sort((a, b) => {
    // isRequiredでまず比較（trueが先）
    if (a.isRequired !== b.isRequired) {
      return a.isRequired ? -1 : 1;
    }
    // 同じグループ内ではorderで比較
    return a.order - b.order;
  });

  return [
    {
      id: 'basic',
      name: '基礎編',
      description: 'プレコンセプションケアの基礎を学ぶコース',
      requiredLectures: basicLectures.filter(l => l.isRequired),
      optionalLectures: basicLectures.filter(l => !l.isRequired),
      requiredDuration: basicLectures
        .filter(l => l.isRequired)
        .reduce((sum, l) => sum + l.duration, 0),
      optionalDuration: basicLectures
        .filter(l => !l.isRequired)
        .reduce((sum, l) => sum + l.duration, 0),
    },
    {
      id: 'advanced',
      name: 'アドバンスト編',
      description: '専門職向けの高度なコース',
      requiredLectures: advancedLectures.filter(l => l.isRequired),
      optionalLectures: advancedLectures.filter(l => !l.isRequired),
      requiredDuration: advancedLectures
        .filter(l => l.isRequired)
        .reduce((sum, l) => sum + l.duration, 0),
      optionalDuration: advancedLectures
        .filter(l => !l.isRequired)
        .reduce((sum, l) => sum + l.duration, 0),
    },
  ];
}

/**
 * 全講座データを取得する共通クエリ
 */
const ALL_LECTURES_QUERY = graphql`
  query AllLectures {
    allMdx(
      filter: {
        frontmatter: { templateKey: { eq: "precon-supporter/lecture-video" } }
      }
      sort: { frontmatter: { order: ASC } }
    ) {
      nodes {
        frontmatter {
          lectureId
          courseId
          title
          videoUrl
          thumbnail
          order
          isRequired
          overview
          instructors {
            name
            affiliation
            type
          }
          targetAudience
        }
      }
    }
  }
`;

/**
 * コース情報を取得するフック
 */
export function useCourse(courseId: 'basic' | 'advanced') {
  const data = useStaticQuery<Queries.AllLecturesQuery>(ALL_LECTURES_QUERY);

  // コースデータをメモ化して、データが変わらない限り同じ参照を返す
  const courses = useMemo(() => buildCoursesFromMdx(data), [data]);
  const course = useMemo(
    () => courses.find(c => c.id === courseId) || null,
    [courses, courseId]
  );

  return useMemo(() => ({ course, loading: false, error: null }), [course]);
}

/**
 * すべてのコース情報を取得するフック
 */
export function useAllCourses() {
  const data = useStaticQuery<Queries.AllLecturesQuery>(ALL_LECTURES_QUERY);

  // コースデータをメモ化して、データが変わらない限り同じ参照を返す
  const courses = useMemo(() => buildCoursesFromMdx(data), [data]);

  return useMemo(() => ({ courses, loading: false, error: null }), [courses]);
}
